import { useCallback, useEffect, useMemo, useState } from "react";
import { Check as CheckIcon, Circle, ClipboardCheck, FileText, FlaskConical, Plus, Server, Target, Wrench, X } from "lucide-react";
import { theme, card, btnPrimary, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { RegisterToolbar, SummaryTiles } from "../../components/RegisterToolbar";
import { downloadCsv, matchesSearch, by, type SortOption } from "../../lib/registerExport";
import { LoadingBlock } from "../../components/Spinner";
import { PortalLink } from "../../components/PortalLink";
import { RulerProgress } from "../../components/Drawing";
import { NextSteps, type NextStepItem } from "../../components/NextSteps";
import { DetailFields, EditButton, RecordActions } from "../../components/RecordView";
import { ModalOverlay, ModalHeader, Field, MultiPick, EmptyState, SeverityPill, StatusPill, input, textarea, secondaryBtn, dangerBtn, toDateInput, fromInput, fmtDateOnly, todayInput, type Exercise } from "../resilience/shared";
import {
  TEST_TYPES, REQUIRED_TEST_TYPES, testTypeLabel, TEST_STATUS, TONE, DAY, Pill, Section, ErrorText, useSaver, AlertList, CountBadge,
  CorrectiveActionList, RaiseCorrectiveAction, byCriticality, th, td, theadRow, DateCell, actionRef,
  type DoraContext, type DoraTest, type TestsResponse, type Tone,
} from "./shared";

export type TestFilter = "all" | "overdue" | "upcoming" | "completed" | "findings";
export const TEST_FILTERS: TestFilter[] = ["all", "overdue", "upcoming", "completed", "findings"];
const iso = (v: string | null) => (v ? v.slice(0, 10) : "");
const OUTCOME_LABEL: Record<string, string> = { met: "Objectives met", partially_met: "Partially met", not_met: "Not met" };
/** Findings recorded and no corrective action raised yet. */
const findingsOpen = (t: DoraTest) => (t.findingsCount ?? 0) + (t.criticalFindings ?? 0) > 0 && t.correctiveActions.length === 0;

interface Props {
  canEdit: boolean;
  context: DoraContext | null;
  openId: string | null;
  onOpen: (id: string | null) => void;
  filter: TestFilter;
  onFilter: (f: TestFilter) => void;
  onChanged: () => void;
}

const isOverdue = (t: DoraTest) => t.status === "OVERDUE" || ((t.status === "SCHEDULED" || t.status === "IN_PROGRESS") && new Date(t.scheduledDate).getTime() < Date.now());

export function DoraTestsTab({ canEdit, context, openId, onOpen, filter, onFilter, onChanged }: Props) {
  const [data, setData] = useState<TestsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("scheduled");

  const sortOptions: SortOption<DoraTest>[] = useMemo(() => [
    { key: "scheduled", label: "Scheduled date", compare: by.date((t) => t.scheduledDate, "asc") },
    { key: "recent", label: "Most recently completed", compare: by.date((t) => t.completedDate, "desc") },
    { key: "type", label: "Test type", compare: by.text((t) => testTypeLabel(t.testType)) },
    { key: "critical", label: "Most critical findings", compare: by.number((t) => t.criticalFindings, "desc") },
  ], []);

  const load = useCallback(async () => {
    try { setData(await api.get<TestsResponse>("/dora/tests")); setError(null); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the testing programme")); setData({ tests: [], programmeAlerts: [], coverage: { important: 0, testedInYear: 0, untested: [] } }); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const selected = useMemo(() => data?.tests.find((t) => t.id === openId) ?? null, [data, openId]);
  // Opening a test shows the record; Edit swaps in the form, and a different record (or none) resets it.
  const [editing, setEditing] = useState(false);
  useEffect(() => { setEditing(false); }, [openId]);
  const reload = async () => { await load(); onChanged(); };

  if (!data) return <LoadingBlock />;
  const { tests, coverage, programmeAlerts } = data;
  const overdue = tests.filter(isOverdue);
  const upcoming = tests.filter((t) => !isOverdue(t) && t.status !== "COMPLETED");
  const completed = tests.filter((t) => t.status === "COMPLETED");
  const withFindings = tests.filter(findingsOpen);
  const byFilter = filter === "overdue" ? overdue : filter === "upcoming" ? upcoming : filter === "completed" ? completed : filter === "findings" ? withFindings : tests;
  const shown = byFilter
    .filter((t) => matchesSearch(search, testTypeLabel(t.testType), t.provider, t.scope, t.notes, ...t.assets.map((a) => a.asset.name)))
    .sort((sortOptions.find((o) => o.key === sort) ?? sortOptions[0]).compare);
  const toggle = (f: TestFilter) => onFilter(filter === f ? "all" : f);

  function exportCsv() {
    downloadCsv("dora-resilience-tests", [
      { header: "Test type", value: (t) => testTypeLabel(t.testType) },
      { header: "Status", value: (t) => (isOverdue(t) ? "OVERDUE" : t.status) },
      { header: "Scheduled", value: (t) => iso(t.scheduledDate) },
      { header: "Completed", value: (t) => iso(t.completedDate) },
      { header: "Provider", value: (t) => t.provider },
      { header: "Scope", value: (t) => t.scope },
      { header: "Findings", value: (t) => t.findingsCount },
      { header: "Critical findings", value: (t) => t.criticalFindings },
      { header: "Systems covered", value: (t) => t.assets.map((a) => a.asset.name).join("; ") },
      { header: "Corrective actions", value: (t) => t.correctiveActions.length },
    ], shown);
  }
  const pct = coverage.important ? Math.round((coverage.testedInYear / coverage.important) * 100) : 0;
  const covTone: Tone = coverage.important === 0 ? "neutral" : coverage.testedInYear === coverage.important ? "success" : pct >= 50 ? "warning" : "danger";

  const typeRows = [...REQUIRED_TEST_TYPES, "TLPT"].map((type) => {
    const done = tests.filter((t) => t.testType === type && t.status === "COMPLETED" && t.completedDate).sort((a, b) => new Date(b.completedDate!).getTime() - new Date(a.completedDate!).getTime())[0] ?? null;
    const planned = tests.filter((t) => t.testType === type && t.status !== "COMPLETED").sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())[0] ?? null;
    const windowMs = type === "TLPT" ? 3 * 365 * DAY : 365 * DAY;
    const current = !!done && new Date(done.completedDate!).getTime() >= Date.now() - windowMs;
    return { type, done, planned, current };
  });

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
        <div style={{ ...card, padding: 18 }}>
          <div style={{ ...text.statLabel, display: "flex", alignItems: "center", gap: 6 }}><Server size={13} />Systems tested (Art. 24(6))</div>
          <div style={{ ...text.stat, color: covTone === "neutral" ? theme.color.heading : TONE[covTone].color, marginTop: 4 }}>
            {coverage.testedInYear} of {coverage.important}
          </div>
          <div style={{ ...text.hint, marginTop: 2 }}>critical or essential systems tested in the last 12 months</div>
          {coverage.important > 0 && (
            <RulerProgress value={coverage.testedInYear} max={coverage.important} label="Systems tested" style={{ marginTop: 10 }} />
          )}
          {coverage.important === 0 && (
            <div style={{ ...text.hint, color: TONE.warning.color, marginTop: 10 }}>No asset is marked critical or as supporting an essential function, so there is nothing to measure. <PortalLink to="/resilience/assets" style={{ color: "var(--c-accentText)" }}>Review the asset register</PortalLink>.</div>
          )}
          {coverage.untested.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ ...text.label, color: TONE.danger.color, marginBottom: 6 }}>Not tested in 12 months ({coverage.untested.length})</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 140, overflowY: "auto" }}>
                {[...coverage.untested].sort(byCriticality).map((a) => (
                  <PortalLink key={a.id} to={`/resilience/assets?asset=${a.id}`} style={{ textDecoration: "none" }}>
                    <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 2, border: `1px solid ${TONE.danger.border}`, background: TONE.danger.bg, color: TONE.danger.color, display: "inline-block" }}>
                      {a.name}{a.isEssentialFunction ? " · essential" : a.criticality === "critical" ? " · critical" : ""}
                    </span>
                  </PortalLink>
                ))}
              </div>
              <div style={{ ...text.meta, marginTop: 6 }}>Add them to a test's “Systems covered” to count them.</div>
            </div>
          )}
        </div>

        <div style={{ ...card, padding: 18 }}>
          <div style={{ ...text.sectionTitle, margin: 0, display: "flex", alignItems: "center", gap: 6 }}><ClipboardCheck size={13} />Required test types (Art. 25)</div>
          <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
            {typeRows.map((r) => {
              const tone: Tone = r.current ? "success" : r.planned ? "accent" : r.type === "TLPT" ? "neutral" : "danger";
              return (
                <div key={r.type} style={{ ...text.body, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: TONE[tone].color, display: "inline-flex" }}>{r.current ? <CheckIcon size={15} /> : r.planned ? <Circle size={13} /> : <X size={15} />}</span>
                  <span style={{ flex: 1, color: theme.color.text, minWidth: 0 }}>{testTypeLabel(r.type)}{r.type === "TLPT" && <span style={text.meta}> · every 3 years, if designated</span>}</span>
                  <span style={{ ...text.hint, color: TONE[tone].color, whiteSpace: "nowrap" }}>
                    {r.current ? `done ${fmtDateOnly(r.done!.completedDate)}` : r.planned ? `planned ${fmtDateOnly(r.planned.scheduledDate)}` : r.done ? `last ${fmtDateOnly(r.done.completedDate)}` : "none"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {programmeAlerts.length > 0 && (
        <div style={{ ...card, padding: 16 }}>
          <div style={{ ...text.sectionTitle, marginBottom: 10 }}>The programme needs attention</div>
          <AlertList alerts={programmeAlerts} />
        </div>
      )}

      <SummaryTiles tiles={[
        { key: "all", label: "All tests", value: tests.length, active: filter === "all", onClick: () => onFilter("all") },
        { key: "overdue", label: "Overdue", value: overdue.length, tone: overdue.length ? "danger" : "success", active: filter === "overdue", onClick: () => toggle("overdue") },
        { key: "upcoming", label: "Upcoming", value: upcoming.length, tone: "accent", active: filter === "upcoming", onClick: () => toggle("upcoming") },
        { key: "completed", label: "Completed", value: completed.length, tone: "neutral", active: filter === "completed", onClick: () => toggle("completed") },
        { key: "findings", label: "Findings without action", value: withFindings.length, hint: "raise a corrective action", tone: withFindings.length ? "warning" : "neutral", active: filter === "findings", onClick: () => toggle("findings") },
      ]} />
      <RegisterToolbar<DoraTest> search={search} onSearch={setSearch} placeholder="Search tests, provider, scope, systems"
        sortOptions={sortOptions} sort={sort} onSort={setSort} onExport={exportCsv} count={{ shown: shown.length, total: tests.length }}>
        {filter !== "all" && <button type="button" style={secondaryBtn} onClick={() => onFilter("all")}>Clear filter</button>}
        {canEdit && <button type="button" style={btnPrimary} onClick={() => setCreating(true)}><Plus size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Schedule test</button>}
      </RegisterToolbar>
      {error && <div style={{ ...card, ...text.body, padding: 12, color: theme.color.dangerText }}>{error}</div>}

      {shown.length === 0 ? (
        <div style={card}>
          <EmptyState title={search ? "No tests match your search" : filter === "all" ? "No tests scheduled" : "Nothing here"} detail="DORA expects a risk-based testing programme: each required test type at least yearly, covering every system that supports a critical or important function. Completed tests are written to the Evidence Repository." action={canEdit && filter === "all" ? <button type="button" style={btnPrimary} onClick={() => setCreating(true)}>Schedule the first test</button> : undefined} />
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={theadRow}>{["Test", "Status", "Scheduled", "Completed", "Findings", "Systems", "To do"].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {shown.map((t) => {
                const late = isOverdue(t);
                const st = late ? TEST_STATUS.OVERDUE : TEST_STATUS[t.status] ?? { tone: "neutral" as Tone, label: t.status };
                const breaches = t.alerts.filter((a) => a.severity === "breach").length;
                const warnings = t.alerts.length - breaches;
                const findingsNoAction = findingsOpen(t);
                return (
                  <tr key={t.id} onClick={() => onOpen(t.id)} style={{ cursor: "pointer", borderBottom: "1px solid var(--c-border)" }}>
                    <td style={{ ...td, minWidth: 200 }}>
                      <div style={text.itemTitle}>{testTypeLabel(t.testType)}</div>
                      {(t.provider || t.scope) && <div style={{ ...text.hint, marginTop: 2 }}>{[t.provider, t.scope].filter(Boolean).join(" · ").slice(0, 120)}</div>}
                    </td>
                    <td style={td}><Pill tone={st.tone}>{st.label}</Pill></td>
                    <td style={td}>{t.status === "COMPLETED" ? <span style={{ color: theme.color.muted }}>{fmtDateOnly(t.scheduledDate)}</span> : <DateCell iso={t.scheduledDate} warnDays={14} />}</td>
                    <td style={{ ...td, color: theme.color.muted, whiteSpace: "nowrap" }}>{fmtDateOnly(t.completedDate)}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      {t.findingsCount == null && t.criticalFindings == null ? <span style={{ color: theme.color.faint }}>—</span> : (
                        <span>
                          {t.findingsCount ?? 0}
                          {(t.criticalFindings ?? 0) > 0 && <span style={{ marginLeft: 6, fontWeight: 700, color: TONE.danger.color }}>{t.criticalFindings} critical</span>}
                        </span>
                      )}
                    </td>
                    <td style={{ ...td, color: t.assets.length ? theme.color.text : TONE.warning.color }}>{t.assets.length || "None"}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                        <CountBadge n={breaches} tone="danger" title="Deadline breaches" />
                        <CountBadge n={warnings} tone="warning" title="Warnings" />
                        {findingsNoAction && <Pill tone="warning" title="Findings recorded but no corrective action raised">No action</Pill>}
                        {t.correctiveActions.length > 0 && <span style={{ ...text.meta, color: theme.color.muted }}>{t.correctiveActions.length} action{t.correctiveActions.length === 1 ? "" : "s"}</span>}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <TestModal key="new" test={null} context={context}
          onClose={() => setCreating(false)}
          onSaved={async (id) => { setCreating(false); await reload(); onOpen(id); }}
          onDeleted={async () => { setCreating(false); }} />
      )}
      {!creating && selected && editing && canEdit && (
        <TestModal key={`edit-${selected.id}`} test={selected} context={context}
          onClose={() => setEditing(false)}
          onSaved={async () => { await reload(); setEditing(false); }}
          onDeleted={async () => { setEditing(false); onOpen(null); await reload(); }} />
      )}
      {!creating && selected && !(editing && canEdit) && (
        <TestView key={`view-${selected.id}`} test={selected} canEdit={canEdit}
          onClose={() => onOpen(null)} onEdit={() => setEditing(true)} reload={reload} />
      )}
    </div>
  );
}

/**
 * What a complete test record holds (DORA Art. 24–26): its scope and the assets it covered, who ran
 * it, once done the date, the findings and the corrective actions they need, and the report on file.
 */
function testSteps(t: DoraTest): NextStepItem[] {
  const done = t.status === "COMPLETED";
  const late = isOverdue(t);
  return [
    { done: !!t.scope?.trim(), label: "Describe the scope of the test" },
    { done: t.assets.length > 0, label: "Link the ICT assets it covers", hint: "Critical and essential assets need a test every year." },
    { done: !!t.provider?.trim(), label: "Name who carries out the test", hint: "An internal team or an independent tester." },
    { done: done, label: late ? "Carry out the test (it is overdue)" : "Carry out the test and record when it was completed", urgent: late },
    ...(done ? [
      { done: t.findingsCount != null, label: "Record the number of findings" },
      ...(findingsOpen(t) || t.correctiveActions.length > 0 ? [{ done: !findingsOpen(t), label: "Raise corrective actions for the findings", urgent: findingsOpen(t) }] : []),
      { done: !!t.evidenceDocumentId, label: "Put the test report on file" },
    ] : []),
  ];
}

/** Opening a test: the read-only record, with Edit and the state changes (Actions) for those who may make them. */
function TestView({ test, canEdit, onClose, onEdit, reload }: {
  test: DoraTest; canEdit: boolean; onClose: () => void; onEdit: () => void; reload: () => Promise<void>;
}) {
  const saver = useSaver();
  const late = isOverdue(test);
  const st = late ? TEST_STATUS.OVERDUE : TEST_STATUS[test.status] ?? { tone: "neutral" as Tone, label: test.status };
  const findings = Math.max(test.findingsCount ?? 0, test.criticalFindings ?? 0);
  const sortedAssets = [...test.assets.map((a) => a.asset)].sort(byCriticality);
  const important = sortedAssets.filter((a) => a.criticality === "critical" || a.isEssentialFunction).length;

  async function move(body: Record<string, unknown>) {
    await saver.run(async () => { await api.put<{ test: DoraTest }>(`/dora/tests/${test.id}`, body); await reload(); }, "Could not update the test");
  }
  async function recordExercise() {
    await saver.run(async () => { await api.post(`/dora/tests/${test.id}/exercise`, {}); await reload(); }, "Could not record the test in the exercise log");
  }
  const ex = test.exercise;
  const today = todayInput();

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader
        title={<span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>{testTypeLabel(test.testType)}<Pill tone={st.tone}>{st.label}</Pill></span>}
        onClose={onClose}
        right={canEdit ? <EditButton onClick={onEdit} /> : undefined}
      />
      <div style={{ padding: 20, display: "grid", gap: 18 }}>
        <AlertList alerts={test.alerts} />

        <NextSteps items={testSteps(test)} action={canEdit ? <button type="button" style={secondaryBtn} onClick={onEdit}>Fill these in</button> : undefined} />

        <Section title="The test" icon={<Target size={15} />}>
          <DetailFields fields={[
            { label: "Type", value: testTypeLabel(test.testType) },
            { label: "Status", value: <Pill tone={st.tone}>{st.label}</Pill> },
            { label: "Scheduled for", value: fmtDateOnly(test.scheduledDate) },
            { label: "Completed on", value: test.completedDate ? fmtDateOnly(test.completedDate) : null },
            { label: "Provider", value: test.provider },
            { label: "Scope", value: test.scope, wide: true, multiline: true },
          ]} />
        </Section>

        <Section title={`Systems covered (${test.assets.length})`} icon={<Server size={15} />}
          help={`Each critical or essential system needs a completed test every 12 months (Art. 24(6)). ${important} of these systems count towards that.`}>
          {sortedAssets.length === 0 ? <div style={{ ...text.body, color: TONE.warning.color }}>None linked.</div> : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {sortedAssets.map((a) => (
                <PortalLink key={a.id} to={`/resilience/assets?asset=${a.id}`} style={{ textDecoration: "none" }}>
                  <span style={{ ...text.hint, padding: "3px 10px", borderRadius: 2, display: "inline-block", border: "1px solid var(--c-border)", background: theme.color.panelAlt, color: theme.color.text }}>
                    {a.name} · {a.criticality}{a.isEssentialFunction ? " · essential" : ""}
                  </span>
                </PortalLink>
              ))}
            </div>
          )}
        </Section>

        <Section title="Findings" icon={<FileText size={15} />}>
          <DetailFields fields={[
            { label: "Findings", value: test.findingsCount != null ? String(test.findingsCount) : null },
            { label: "Of which critical", value: test.criticalFindings != null ? <span style={{ fontWeight: (test.criticalFindings ?? 0) > 0 ? 700 : 400, color: (test.criticalFindings ?? 0) > 0 ? TONE.danger.color : undefined }}>{test.criticalFindings}</span> : null },
            { label: "Notes", value: test.notes, wide: true, multiline: true },
          ]} />
        </Section>

        <Section title="Resilience exercise" icon={<FlaskConical size={15} />}
          help={ex ? "The test was run as this exercise: its findings, and the corrective action each one has, are recorded there." : undefined}>
          {!ex ? (
            <div style={{ ...text.body, color: theme.color.muted }}>Not recorded in the resilience exercise log. Recorded there, each finding can be raised with its own corrective action.</div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <PortalLink to={`/resilience/exercises?exercise=${encodeURIComponent(ex.id)}`} style={{ ...text.itemTitle, color: "var(--c-accentText)" }}>{ex.title}</PortalLink>
                <StatusPill value={ex.status} />
                <span style={text.hint}>{ex.outcome ? OUTCOME_LABEL[ex.outcome] ?? ex.outcome : "No outcome recorded"}{ex.completedDate ? ` · completed ${fmtDateOnly(ex.completedDate)}` : ""}</span>
              </div>
              {ex.findingRows.length === 0 ? (
                <div style={{ ...text.hint }}>No findings recorded on the exercise{(ex.findingsCount ?? 0) > 0 ? ` (it counts ${ex.findingsCount})` : ""}.</div>
              ) : (
                <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr style={theadRow}>{["Finding", "Severity", "Corrective action"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {ex.findingRows.map((r) => (
                      <tr key={r.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                        <td style={td}>{r.title}</td>
                        <td style={td}><SeverityPill value={r.severity} /></td>
                        <td style={td}>
                          {r.correctiveAction ? (
                            <PortalLink to={`/improvement?action=${encodeURIComponent(r.correctiveAction.id)}`} style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span style={{ ...text.ref, color: theme.color.muted }}>{actionRef(r.correctiveAction)}</span>
                              <span style={{ color: "var(--c-accentText)" }}>{r.correctiveAction.title}</span>
                              <StatusPill value={r.correctiveAction.status} />
                            </PortalLink>
                          ) : <span style={{ color: TONE.warning.color }}>No corrective action raised</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </Section>

        <Section title="Corrective actions" icon={<Wrench size={15} />} help="Every finding needs a remediation owner and date (Art. 24(5)). Actions are tracked on the improvement programme.">
          <CorrectiveActionList actions={test.correctiveActions}
            intro={findings > 0 && test.correctiveActions.length === 0 ? (
              <div style={{ ...text.body, color: TONE.warning.color, background: TONE.warning.bg, borderRadius: 3, padding: "8px 12px" }}>
                {findings} finding{findings === 1 ? "" : "s"}{(test.criticalFindings ?? 0) > 0 ? `, ${test.criticalFindings} critical,` : ""} recorded with no corrective action. Raise one for these findings.
              </div>
            ) : undefined} />
        </Section>

        {canEdit && (
          <RecordActions>
            {test.status !== "COMPLETED" && test.status !== "IN_PROGRESS" && (
              <button type="button" style={secondaryBtn} disabled={saver.busy} onClick={() => move({ status: "IN_PROGRESS" })}>Mark in progress</button>
            )}
            {test.status !== "COMPLETED" && (
              <button type="button" style={btnPrimary} disabled={saver.busy} onClick={() => move({ status: "COMPLETED", completedDate: fromInput(toDateInput(test.completedDate) || today) })}>Mark completed today</button>
            )}
            {!test.exerciseId && (
              <button type="button" style={secondaryBtn} disabled={saver.busy} onClick={recordExercise}><FlaskConical size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Record in the exercise log</button>
            )}
            <RaiseCorrectiveAction
              source="exercise" sourceRef={`dora-test:${test.id}`}
              defaultTitle={`Remediate the findings of the ${testTypeLabel(test.testType)}${test.completedDate ? ` (${fmtDateOnly(test.completedDate)})` : ""}`}
              defaultPriority={(test.criticalFindings ?? 0) > 0 ? "critical" : "high"}
              assetOptions={test.assets.map((a) => ({ id: a.asset.id, name: a.asset.name }))}
              onCreated={reload} />
          </RecordActions>
        )}
        <ErrorText error={saver.error} />
      </div>
    </ModalOverlay>
  );
}

/** The existing test form: scheduling a new test, or Edit from the record view (Save returns to the view). */
function TestModal({ test, context, onClose, onSaved, onDeleted }: {
  test: DoraTest | null; context: DoraContext | null;
  onClose: () => void; onSaved: (id: string) => Promise<void>; onDeleted: () => Promise<void>;
}) {
  const [f, setF] = useState({
    testType: test?.testType ?? "VULNERABILITY_SCAN",
    status: test?.status ?? "SCHEDULED",
    scheduledDate: toDateInput(test?.scheduledDate ?? null),
    completedDate: toDateInput(test?.completedDate ?? null),
    provider: test?.provider ?? "",
    scope: test?.scope ?? "",
    findingsCount: test?.findingsCount?.toString() ?? "",
    criticalFindings: test?.criticalFindings?.toString() ?? "",
    notes: test?.notes ?? "",
    assetIds: test?.assets.map((a) => a.asset.id) ?? [],
    exerciseId: test?.exerciseId ?? "",
  });
  const saver = useSaver();
  // Exercises not yet the record of another test (or of this one) can be the exercise it was run as.
  const [exercises, setExercises] = useState<Exercise[] | null>(null);
  useEffect(() => {
    api.get<{ exercises: Exercise[] }>("/resilience/exercises").then((d) => setExercises(d.exercises)).catch(() => setExercises([]));
  }, []);
  const exerciseOptions = (exercises ?? []).filter((e) => e.id === f.exerciseId || ((!e.doraTest || e.doraTest.id === test?.id) && e.status !== "cancelled"));
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((p) => ({ ...p, [k]: v }));
  const assets = [...(context?.assets ?? [])].sort(byCriticality);
  const assetOptions = assets.map((a) => ({ id: a.id, name: `${a.name} · ${a.criticality}${a.isEssentialFunction ? " · essential" : ""}` }));
  const importantIds = new Set(assets.filter((a) => a.criticality === "critical" || a.isEssentialFunction).map((a) => a.id));
  const importantSelected = f.assetIds.filter((id) => importantIds.has(id)).length;

  async function save(overrides: Partial<typeof f> = {}) {
    const v = { ...f, ...overrides };
    if (!v.scheduledDate) { saver.setError("Enter the scheduled date"); return; }
    const num = (s: string) => (s.trim() === "" ? null : Number(s));
    if ([v.findingsCount, v.criticalFindings].some((s) => s.trim() !== "" && !/^\d+$/.test(s.trim()))) { saver.setError("Findings must be whole numbers"); return; }
    let completedDate = v.completedDate;
    if (v.status === "COMPLETED" && !completedDate) completedDate = todayInput();
    const body = {
      testType: v.testType, status: v.status, scheduledDate: fromInput(v.scheduledDate), completedDate: fromInput(completedDate),
      provider: v.provider.trim() || null, scope: v.scope.trim() || null, findingsCount: num(v.findingsCount), criticalFindings: num(v.criticalFindings),
      notes: v.notes.trim() || null, assetIds: v.assetIds, exerciseId: v.exerciseId || null,
    };
    await saver.run(async () => {
      const r = test ? await api.put<{ test: DoraTest }>(`/dora/tests/${test.id}`, body) : await api.post<{ test: DoraTest }>("/dora/tests", body);
      setF((p) => ({ ...p, ...overrides, completedDate }));
      await onSaved(r.test.id);
    }, "Could not save the test");
  }

  async function remove() {
    if (!test || !window.confirm(`Delete this ${testTypeLabel(test.testType)}?`)) return;
    await saver.run(async () => { await api.del(`/dora/tests/${test.id}`); await onDeleted(); }, "Could not delete the test");
  }

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader
        title={test ? `Edit ${testTypeLabel(test.testType).toLowerCase()}` : "Schedule a resilience test"}
        onClose={onClose}
        right={test ? <button type="button" style={dangerBtn} disabled={saver.busy} onClick={remove}>Delete</button> : undefined}
      />
      <div style={{ padding: 20, display: "grid", gap: 18 }}>
        <Section title="The test" icon={<Target size={15} />}>
          <fieldset style={{ border: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            <Field label="Type">
              <select style={input} value={f.testType} onChange={(e) => set("testType", e.target.value)}>
                {TEST_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select style={input} value={f.status} onChange={(e) => set("status", e.target.value)}>
                {Object.entries(TEST_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Scheduled for"><input type="date" style={input} value={f.scheduledDate} onChange={(e) => set("scheduledDate", e.target.value)} /></Field>
            <Field label="Completed on" help={f.status === "COMPLETED" && !f.completedDate ? "Left empty, today is used." : undefined}><input type="date" style={input} value={f.completedDate} onChange={(e) => set("completedDate", e.target.value)} /></Field>
            <Field label="Provider" help="Internal team or the testing firm."><input style={input} value={f.provider} onChange={(e) => set("provider", e.target.value)} maxLength={200} /></Field>
            <Field label="Scope" span2><textarea style={textarea} rows={2} value={f.scope} onChange={(e) => set("scope", e.target.value)} maxLength={2000} /></Field>
            <Field label="Run as the resilience exercise (optional)" span2 help="Its findings, and the corrective action each one has, are then shown on this test.">
              <select style={input} value={f.exerciseId} onChange={(e) => set("exerciseId", e.target.value)} disabled={exercises === null}>
                <option value="">{exercises === null ? "Loading exercises…" : "None"}</option>
                {exerciseOptions.map((e) => <option key={e.id} value={e.id}>{e.title} · {fmtDateOnly(e.completedDate ?? e.scheduledDate)}</option>)}
              </select>
            </Field>
          </fieldset>
        </Section>

        <Section title={`Systems covered (${f.assetIds.length})`} icon={<Server size={15} />}
          help={`Each critical or essential system needs a completed test every 12 months (Art. 24(6)). ${importantSelected} of the selected systems count towards that.`}>
          <div style={{ maxHeight: 200, overflowY: "auto", padding: 2 }}><MultiPick options={assetOptions} selected={f.assetIds} onChange={(ids) => set("assetIds", ids)} empty="No active assets in the asset register yet." /></div>
        </Section>

        <Section title="Findings" icon={<FileText size={15} />}>
          <fieldset style={{ border: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            <Field label="Findings"><input style={input} inputMode="numeric" value={f.findingsCount} onChange={(e) => set("findingsCount", e.target.value)} /></Field>
            <Field label="Of which critical"><input style={{ ...input, ...(Number(f.criticalFindings) > 0 ? { borderColor: theme.color.danger } : {}) }} inputMode="numeric" value={f.criticalFindings} onChange={(e) => set("criticalFindings", e.target.value)} /></Field>
            <Field label="Notes" span2><textarea style={textarea} rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          </fieldset>
        </Section>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button type="button" style={btnPrimary} disabled={saver.busy} onClick={() => save()}>{saver.busy ? "Saving…" : test ? "Save test" : "Schedule test"}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
        <ErrorText error={saver.error} />
      </div>
    </ModalOverlay>
  );
}
